import React, { useState, useEffect, JSX } from 'react'
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";
import { injectIntl, FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { DataGrid, Column, Export, GroupPanel, Grouping, FilterRow, Pager, Paging, StateStoring, HeaderFilter, Selection, Sorting, DataGridTypes } from 'devextreme-react/data-grid';
import { CreateUrl, CallGetAPI, CallPutAPI, CallPostAPI } from 'Utils/ApiHelper.js';
import Loader from '../../Utils/Loader/NewLoader2025'
import { useTabletContext, useMobileLandscape } from '../../Context/ContextProvider'
import { Redirect } from "react-router-dom";
import moment from 'moment';
import { adjustTime } from '../../Utils/AdjustTime'
import { Chip, useTheme, Menu, MenuItem, Button } from '@mui/material';
import { isLCLLogger } from 'variables/chariotProductIds';

interface RedirectProps {
    serial: number | undefined;
}

interface DeviceData {
    id: number;
    serial: string;
    site: string;
    model: string;
    channels: string;
    lastConnected: Date | null;
    alarmState: string;
    siteId: number | null;
    siteName: string;
    currentFw: string;
    availableFw: string;
    company: string;
    firmwareToUpdate: number | null;
    firmwareSent: number | null;
    firmwareConfirmed: Date | null;
    firmwareSize: number | null;
}



const FirmwareUpdatesMobile: React.FC = () => {
    //hooks
    const intl = useIntl()
    const isTablet = useTabletContext()
    const isLandscape = useMobileLandscape()
    const theme = useTheme()

    //states
    const [loading, setLoading] = useState<boolean>(true)
    const [redirect, setRedirect] = useState<boolean>(false)
    const [redirectPath, setRedirectPath] = useState<string>("")
    const [redirectProps, setRedirectProps] = useState<RedirectProps | null>()
    const [deviceData, setDeviceData] = useState<Array<DeviceData>>()


    //useEffect
    useEffect(() => {
        reloadData()
    }, [])


    const reloadData = () => {
        CallGetAPI(CreateUrl('/api/aquaguard/GetFirmwareUpdateLoggers?companyId=' + sessionStorage.getItem('companyId') + '&filterGroup=' + sessionStorage.getItem('filterGroupId') + "&deployed=false"), {})
            .then(data => {
                const deviceDataTemp: DeviceData[] | null = []
                for (let i = 0; i < data.length; i++) {
                    const serial = data[i].serial;
                    const id = parseInt(data[i].id);
                    const rec: DeviceData = {
                        id: id,
                        serial: serial,
                        site: data[i].site.replace(/[^A-Za-z0-9]/g, ''),
                        model: data[i].model,
                        channels: data[i].channels,
                        lastConnected: data[i].lastConnected ? adjustTime(moment(data[i].lastConnected).toDate()) : null,
                        alarmState: data[i].alarmState.toString(),
                        siteId: data[i].siteId,
                        siteName: data[i].siteName,
                        currentFw: data[i].currentFirmware,
                        availableFw: data[i].availableFirmware,
                        company: data[i].company,
                        firmwareToUpdate: data[i].firmwareToUpdate,
                        firmwareSent: data[i].firmwareSent,
                        firmwareConfirmed: adjustTime(data[i].firmwareConfirmed),
                        firmwareSize: data[i].firmwareSize,
                    };
                    deviceDataTemp.push(rec);

                }
                setDeviceData(deviceDataTemp)
                setLoading(false)
            })
            .catch(err => {
                console.error(err)
                setDeviceData([])
                setLoading(false)
            })
    }

    const deviceOverviewClick = (serial: number | undefined, model: string | undefined): void => {
        console.log(serial, model)
        setRedirect(true)
        setRedirectPath(!isLCLLogger(model) ? '/portal/DeviceDetail' : '/portal/LCLDeviceDetail')
        setRedirectProps({ serial: serial })
    }

    const serialColumnTemplate = (item: DataGridTypes.ColumnCellTemplateData): JSX.Element => {
        console.log(item)

        return (<Chip
            label={item.data.serial}
            sx={{ backgroundColor: theme.palette.grey.main, color: theme.palette.grey.contrastText }}
            onClick={(): void => deviceOverviewClick(item.data.merial, item.data.model)}
        //onContextMenu={(e): void => handleContextMenu(e, item.data.serial, item.data.model)}
        />)
    }

    const renderOverviewButton = (data: DataGridTypes.ColumnCellTemplateData): JSX.Element => {

        return (!isTablet && !isLandscape ?
            <Button color="secondary" variant="contained" onClick={(): void => deviceOverviewClick(data.data.serial, data.data.model)}><FormattedMessage id='Overview' /></Button> :
            <div style={{ display: "flex", justifyContent: "center" }}>
                <Button color="secondary" variant="contained" onClick={(): void => deviceOverviewClick(data.data.serial, data.data.model)}><FormattedMessage id='Overview' /></Button>
            </div>)
    }

    return (
        <div>
            {loading ? <div style={{
                position: 'relative', left: 'Calc(50% - 100px)', minHeight: 200
            }}>
                <Loader color={undefined} />
            </div> :
                !redirect ? (<div>
                    <Card>
                        <CardHeader color="primary">
                            <h4 style={{ textAlign: "center" }}>{intl.formatMessage({ id: 'Devices where newer firmware is available.' })}</h4>
                        </CardHeader>
                        <CardBody>
                            <DataGrid
                                dataSource={deviceData}>
                                <Column dataField="Serial" dataType="string" cellRender={(data): JSX.Element => serialColumnTemplate(data)} caption={intl.formatMessage({ id: "Serial" })} width={77} />
                                {isTablet && <Column dataField="siteName" caption={intl.formatMessage({ id: "Site Ref" })} />}
                                {isTablet && <Column dataField="siteNameUserFriendly" caption={intl.formatMessage({ id: "Site Name" })} />}
                                <Column dataField="currentFw" width={!isLandscape && !isTablet ? 50 : 'inherit'} caption={intl.formatMessage({ id: "Latest Battery" })} allowHeaderFiltering={true} />
                                <Column
                                    dataField="LastConnected"
                                    customizeText={(cellInfo) => {
                                        return moment(cellInfo.value).format("DD MMM HH:mm");
                                    }}
                                    width={!isLandscape && !isTablet ? 80 : 'inherit'}
                                    caption={intl.formatMessage({ id: "Last Connected" })}
                                    allowHeaderFiltering={true}
                                    format="date"
                                />
                                <Column cellRender={(data): JSX.Element => renderOverviewButton(data)} caption={intl.formatMessage({ id: "Details" })} />
                            </DataGrid>
                        </CardBody>
                    </Card>
                </div>) : 
                <Redirect push to={{
                    pathname: redirectPath,
                    state: redirectProps
                }} />}
        </div>
    )
}
export default FirmwareUpdatesMobile